<div class="profile-page">
  @if (user) {

    <!-- ── HERO CARD ── -->
    <div class="card border-0 shadow-sm overflow-hidden">

      <!-- Gradient header -->
      <div class="avatar-hero">
        <button class="btn-edit-fab" (click)="toggleEdit()" title="Edit profile">
          <i class="bi" [ngClass]="isEditing ? 'bi-x-lg' : 'bi-pencil'"></i>
        </button>

        <div class="avatar-container position-relative">
          @if (appService.currentUser?.image_id; as imgId) {
            <img [src]="mediaService.getURLByUUID(imgId)" class="avatar-ring" alt="avatar" />
          } @else {
            <div class="avatar-initials">
              {{ initials }}
            </div>
          }

          <!-- Circular camera upload badge overlay -->
          <label class="avatar-upload-badge" [class.disabled]="uploadingPhoto" title="Upload new photo">
            @if (uploadingPhoto) {
              <span class="spinner-border spinner-border-sm text-primary" role="status" aria-hidden="true"></span>
            } @else {
              <i class="bi bi-camera-fill"></i>
            }
            <input type="file"
                   accept="image/png, image/jpeg, image/jpg, image/webp"
                   class="d-none"
                   (change)="onAvatarSelected($event)"
                   [disabled]="uploadingPhoto" />
          </label>

          <!-- Remove photo badge button if avatar exists -->
          @if (appService.currentUser?.image_id && !uploadingPhoto) {
            <button type="button" class="avatar-remove-badge" (click)="onRemoveAvatar()" title="Remove profile photo">
              <i class="bi bi-trash-fill"></i>
            </button>
          }
        </div>

        <div class="user-name">{{ user.firstname }} {{ user.lastname }}</div>
        <div class="user-email">{{ user.email }}</div>
      </div>

      <!-- ── TABS ── -->
      <ul class="nav profile-tabs px-4 pt-3 bg-white">
        <li class="nav-item">
          <button class="nav-link" [class.active]="activeTab === 'profile'" (click)="activeTab = 'profile'">
            <i class="bi bi-person"></i>Profile
          </button>
        </li>
        <li class="nav-item">
          <button class="nav-link" [class.active]="activeTab === 'password'" (click)="activeTab = 'password'; initPasswordForm()">
            <i class="bi bi-shield-lock"></i>Security
          </button>
        </li>
      </ul>

      <!-- ── PROFILE TAB ── -->
      @if (activeTab === 'profile') {
        <div class="card-body px-4 pb-4 bg-white">

          @if (!isEditing) {
            <!-- READ-ONLY view -->
            <div class="mt-2">
              <div class="field-row">
                <span class="field-label">First Name</span>
                <span class="field-value">{{ user.firstname || '—' }}</span>
              </div>
              <div class="field-row">
                <span class="field-label">Last Name</span>
                <span class="field-value">{{ user.lastname || '—' }}</span>
              </div>
              <div class="field-row">
                <span class="field-label">Phone</span>
                <span class="field-value">{{ user.phone || '—' }}</span>
              </div>
              <div class="field-row">
                <span class="field-label">Email</span>
                <span class="field-value text-muted fst-italic">{{ user.email }}</span>
              </div>
            </div>
          } @else {
            <!-- EDIT FORM -->
            <form [formGroup]="userForm" class="mt-3" (ngSubmit)="onSaveUser()">
              <div class="row g-3">
                <div class="col-md-6">
                  <label class="form-label small fw-semibold text-muted">First Name</label>
                  <input formControlName="firstname" type="text" class="form-control"
                    [class.is-invalid]="isInvalid('firstname', userForm)" placeholder="First name" />
                  <div class="invalid-feedback">First name is required.</div>
                </div>
                <div class="col-md-6">
                  <label class="form-label small fw-semibold text-muted">Last Name</label>
                  <input formControlName="lastname" type="text" class="form-control"
                    [class.is-invalid]="isInvalid('lastname', userForm)" placeholder="Last name" />
                  <div class="invalid-feedback">Last name is required.</div>
                </div>
                <div class="col-12">
                  <label class="form-label small fw-semibold text-muted">Phone</label>
                  <input formControlName="phone" type="tel" class="form-control"
                    [class.is-invalid]="isInvalid('phone', userForm)" placeholder="Phone number" />
                  <div class="invalid-feedback">Phone number is required.</div>
                </div>
              </div>

              <div class="action-bar">
                <button type="button" class="btn btn-light btn-sm" (click)="toggleEdit()">Cancel</button>
                <button type="submit" class="btn btn-primary btn-sm" [disabled]="userForm.invalid || saving">
                  @if (saving) {
                    <span class="spinner-border spinner-border-sm me-1"></span>
                  }
                  Save Changes
                </button>
              </div>
            </form>
          }
        </div>
      }

      <!-- ── SECURITY TAB ── -->
      @if (activeTab === 'password') {
        <div class="card-body px-4 pb-4 bg-white">
          <p class="text-muted small mt-2 mb-4">
            Use a strong password that is at least 6 characters long, and is not used on other sites.
          </p>

          <form [formGroup]="resetPasswordForm" (ngSubmit)="onSavePassword()">
            <div class="mb-3">
              <label class="form-label small fw-semibold text-muted">Current Password</label>
              <div class="input-group">
                <input formControlName="currentPassword"
                  [type]="showCurrent ? 'text' : 'password'"
                  class="form-control"
                  [class.is-invalid]="isInvalid('currentPassword', resetPasswordForm)"
                  placeholder="Enter current password" />
                <button type="button" class="btn btn-outline-secondary" (click)="showCurrent = !showCurrent">
                  <i class="bi" [ngClass]="showCurrent ? 'bi-eye-slash' : 'bi-eye'"></i>
                </button>
                <div class="invalid-feedback">Current password is required.</div>
              </div>
            </div>

            <div class="mb-1">
              <label class="form-label small fw-semibold text-muted">New Password</label>
              <div class="input-group">
                <input formControlName="newPassword"
                  [type]="showNew ? 'text' : 'password'"
                  class="form-control"
                  [class.is-invalid]="isInvalid('newPassword', resetPasswordForm)"
                  placeholder="Enter new password"
                  (input)="onPasswordInput()" />
                <button type="button" class="btn btn-outline-secondary" (click)="showNew = !showNew">
                  <i class="bi" [ngClass]="showNew ? 'bi-eye-slash' : 'bi-eye'"></i>
                </button>
                <div class="invalid-feedback">Password must be at least 6 characters.</div>
              </div>
            </div>

            <!-- Strength meter -->
            <div class="strength-bar mb-1">
              <div class="strength-fill" [style.width.%]="passwordStrength.pct" [style.background]="passwordStrength.color"></div>
            </div>
            <div class="strength-label" [style.color]="passwordStrength.color">{{ passwordStrength.label }}</div>

            <div class="action-bar">
              <button type="button" class="btn btn-light btn-sm" (click)="resetPasswordForm.reset()">Clear</button>
              <button type="submit" class="btn btn-primary btn-sm"
                [disabled]="resetPasswordForm.invalid || saving">
                @if (saving) {
                  <span class="spinner-border spinner-border-sm me-1"></span>
                }
                Update Password
              </button>
            </div>
          </form>
        </div>
      }

    </div>
    <!-- end card -->

  } @else {
    <!-- Skeleton -->
    <div class="card border-0 shadow-sm p-4 text-center text-muted">
      <div class="spinner-border text-primary mx-auto mb-3"></div>
      <p>Loading profile…</p>
    </div>
  }
</div>
